// PdfViewPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { 
  ArrowLeft, 
  Download, 
  Printer, 
  Loader2, 
  FileText,
  ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface PdfViewPageProps {
  pdfId?: string;
}

const PdfViewPage: React.FC<PdfViewPageProps> = ({ pdfId: propPdfId }) => {
  const { pdfId } = useParams<{ pdfId: string }>();
  const navigate = useNavigate();
  const [pdfData, setPdfData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  const actualPdfId = propPdfId || pdfId;

  useEffect(() => {
    if (actualPdfId) {
      fetchPdfData();
    }
  }, [actualPdfId]);

  const fetchPdfData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/pdf/${actualPdfId}`);
      
      if (response.data.success) {
        setPdfData(response.data.data);
        // Create object URL for PDF
        const downloadResponse = await axios.get(
          `/pdf/download/${actualPdfId}`,
          { responseType: 'blob' }
        );
        
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        setError('PDF not found');
      }
    } catch (error: any) {
      console.error('Error fetching PDF:', error);
      setError(error.message || 'Failed to load PDF');
      toast.error('Failed to load PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfData) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfData.fileName || `menu-${actualPdfId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('PDF download started');
  };

  const handlePrint = () => {
    const printWindow = window.open(pdfUrl);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#7BD5B5] mx-auto mb-4" />
          <p className="text-[#687d76]">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">PDF Not Found</h2>
          <p className="text-[#687d76] mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#0A0C0B]">
                  {pdfData?.menuTitle || 'Menu PDF'}
                </h1>
                <p className="text-sm text-[#687d76]">
                  Generated on {new Date(pdfData?.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownload}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              
              {pdfData?.menuId && (
                <a
                  href={`/menu/${pdfData.menuId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Live Menu
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* PDF Viewer */}
      <main className="container mx-auto px-4 py-8">
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {pdfUrl ? (
            <iframe
              src={`${pdfUrl}#view=FitH`}
              className="w-full h-[calc(100vh-200px)]"
              title="PDF Viewer"
            />
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5] mx-auto mb-4" />
                <p className="text-[#687d76]">Loading PDF viewer...</p>
              </div>
            </div>
          )}
        </div>

        {/* PDF Info */}
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#0A0C0B] mb-4">PDF Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#687d76]">Menu</label>
              <p className="text-[#0A0C0B] font-medium">{pdfData?.menuTitle || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#687d76]">Generated</label>
              <p className="text-[#0A0C0B] font-medium">
                {pdfData?.createdAt ? new Date(pdfData.createdAt).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#687d76]">Language</label>
              <p className="text-[#0A0C0B] font-medium">{pdfData?.language || 'Italian'}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PdfViewPage;